import { canOpenCabinetPath, resolveCabinet } from './cabinetAccess';

// Все возможные роли
export const POSITIONS = ['surveyor', 'manager', 'draftsman', 'quality_master', 'head_manager'];

// Куда отправлять каждую роль "по умолчанию"
export const ROLE_HOME_PATH = {
    manager: '/',
    quality_master: '/quality',
    head_manager: '/',
    surveyor: '/',
    draftsman: '/draftsman',
};

// ── ЕДИНЫЙ ИСТОЧНИК ПРАВДЫ: какие роли видят раздел И имеют к нему доступ ──
// Значение = массив ролей. null = «виден всем ролям». Пустой массив = «только админ».
// Меняешь здесь — меняется И меню (Sidebar), И доступ к маршруту (ProtectedRoute).
// Админ (account.role === 'administrator') видит/может всё независимо от этой карты.
export const SECTION_ROLES = {
    '/storage':          null,
    '/':                 ['manager', 'surveyor', 'head_manager'],
    '/draftsman':        ['draftsman'],
    '/constructor':      ['manager', 'head_manager', 'draftsman', 'surveyor'],
    '/applications':     ['manager', 'head_manager'],
    '/chats':            null, // всем
    '/knowledge':        null, // всем
    '/finance':          ['head_manager'],
    '/payments':         ['manager', 'head_manager'],            // ← quality_master убран
    '/client-payments':  ['manager', 'head_manager'],
    '/services-tariffs': ['manager', 'head_manager'],
    '/goals':            ['manager', 'head_manager'],            // ← quality_master убран
    '/quality':          ['quality_master', 'head_manager'],
    '/quality-goals':    ['head_manager'],
    '/clients':          ['manager', 'head_manager'],
    '/documents':        ['manager', 'head_manager'],
    '/performers':       ['manager', 'head_manager'],
    '/statistics':       ['manager', 'head_manager'],            // ← quality_master убран
    '/activity-logs':    ['head_manager'],                       // раздел «Логи» — руководитель/админ
    '/news':             ['head_manager'],
};

// ── Разделы, доступ к которым даёт САМА ДОЛЖНОСТЬ, а не активная роль ──
//
// Обычно маршрут сверяется с активной ролью: у кого две должности, тот сидит
// в одном кабинете и в чужой раздел не ходит. Но «Контроль качества» живёт
// ВНУТРИ кабинета чертёжника — отдельного кабинета контролёра нет и
// переключаться ему некуда. Пункт меню показывался по должности, а маршрут
// проверял активную роль — и у мастера качества с ролью чертёжника раздел
// открывался и тут же выбрасывал обратно в «Сделки».
//
// Сюда попадают только такие разделы-гости. Остальные маршруты остаются
// строгими: кабинеты не смешиваем.
export const POSITION_BASED_SECTIONS = ['/quality'];

export const isPositionBasedPath = (pathname) =>
    POSITION_BASED_SECTIONS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

// Должности аккаунта — всегда массивом.
// position приходит из MultiSelectField: то массивом, то строкой через запятую.
export const positionsOf = (account) => {
    const p = account?.position;
    if (Array.isArray(p)) return p;
    if (typeof p === 'string') return p.split(',').map((s) => s.trim()).filter(Boolean);
    return [];
};

// ── Права по разделам (RBAC). Раздел виден/доступен, только если у аккаунта
// есть указанное право. Админ (role === 'administrator') видит всё. ──
// Дополняется по мере закрытия разделов (следующие этапы).
export const SECTION_PERMISSIONS = {
    '/storage':       'storage.view',
    '/activity-logs': 'activity_log.view',
    '/performers':    'performers.view',
    '/access':        'access.manage',
    '/settings':      'settings.chats.view',
};

// Право, требуемое для пути (точное совпадение важнее префикса). null — не требуется.
export const permissionForPath = (pathname) => {
    if (Object.prototype.hasOwnProperty.call(SECTION_PERMISSIONS, pathname)) {
        return SECTION_PERMISSIONS[pathname];
    }
    const key = Object.keys(SECTION_PERMISSIONS).find(
        (p) => p !== '/' && pathname.startsWith(`${p}/`)
    );
    return key ? SECTION_PERMISSIONS[key] : null;
};

// Есть ли у аккаунта право (админ — всегда да).
export const accountHasPermission = (account, code) => {
    if (!code) return true;
    if (account?.role === 'administrator') return true;
    return Array.isArray(account?.permissions) && account.permissions.includes(code);
};

// Роли, допущенные к пути (точное совпадение приоритетнее префикса).
// Возвращает массив ролей или null (виден всем). undefined-путь → null (не ограничиваем).
export const rolesForPath = (pathname) => {
    if (Object.prototype.hasOwnProperty.call(SECTION_ROLES, pathname)) {
        return SECTION_ROLES[pathname];
    }
    const key = Object.keys(SECTION_ROLES).find(
        (p) => p !== '/' && pathname.startsWith(`${p}/`)
    );
    return key ? SECTION_ROLES[key] : null;
};

// Доступен ли путь по НАБОРУ позиций (union) + админ. Для меню.
export const canAccessPath = (pathname, { account, activeRole } = {}) =>
    canOpenCabinetPath(pathname, account, activeRole,
        rolesForPath(pathname), permissionForPath(pathname));

// Вычислить активную роль
export const getEffectiveRole = (positions, activeRole) =>
    resolveCabinet({ position: positions }, activeRole);

// Получить домашний путь роли
export const getHomePathForRole = (role) => ROLE_HOME_PATH[role] || '/';

// Булевы хелперы — строгое сравнение
export const isManager = (role) => role === 'manager';
export const isSurveyor = (role) => role === 'surveyor';
export const isDraftsman = (role) => role === 'draftsman';
export const isQualityMaster = (role) => role === 'quality_master';
export const isHeadManager = (role) => role === 'head_manager';
export const isAnyManager = (role) => isManager(role) || isHeadManager(role);